import { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { createTeam, deleteTeam, editTeam, getTournamentTeams, getTournamentTeamsByFilter, upsertTeamKits } from 'modules/teams/store/teams/actions';
import type { TeamsSliceState } from './teams-store';

type ReducerBuilder = ActionReducerMapBuilder<TeamsSliceState>;

export function createGetTournamentTeamsReducer(builder: ReducerBuilder) {
  builder.addCase(getTournamentTeams.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(getTournamentTeams.fulfilled, (state, action) => {
    state.isLoading = false;
    state.isLoaded = true;

    const payload = action.payload;
    state.tournamentId = payload.tournamentId;

    const newTeams = payload.teams;
    state.teams = [...state.teams, ...newTeams];
    state.tournamentId = payload.tournamentId;

    if (newTeams.length < state.limit) {
      state.allDataLoaded = true;
    }
  });

  builder.addCase(getTournamentTeams.rejected, (state, action) => {
    state.isLoading = false;
    state.isLoaded = false;
    state.error = action.error.message ?? null;
  });
}

export function createGetTournamentTeamsByFilterReducer(builder: ReducerBuilder) {
  builder.addCase(getTournamentTeamsByFilter.fulfilled, (state, action) => {
    state.filter.teams = action.payload;
    state.filter.allDataLoaded = action.payload.length < state.limit;
  });
}

export function createCreateTeamReducer(builder: ReducerBuilder) {
  builder.addCase(createTeam.pending, (state) => {
    state.processing = true;
    state.processedName = 'createTeam';
  });

  builder.addCase(createTeam.fulfilled, (state, action) => {
    state.processing = false;
    state.processedName = undefined;

    const payload = action.payload;
    state.teams = [...state.teams, payload];
  });

  builder.addCase(createTeam.rejected, (state, action) => {
    state.processing = false;
    state.processedName = undefined;
    state.error = action.error.message || 'Unknown error';
  });
}

export function createEditTeamReducer(builder: ReducerBuilder) {
  builder.addCase(editTeam.pending, (state) => {
    state.processing = true;
    state.processedName = 'editTeam';
  });

  builder.addCase(editTeam.fulfilled, (state, action) => {
    state.processing = false;
    state.processedName = undefined;

    const payload = action.payload;
    const existTeam = state.teams.find((team) => team.id === payload.id);

    if (existTeam) {
      const index = state.teams.indexOf(existTeam);
      const updated = { ...state.teams[index], name: payload.name, shortName: payload.shortName } as (typeof state.teams)[number];
      if (payload.logo !== undefined) {
        (updated as any).logo = payload.logo as any;
      }
      state.teams[index] = updated;
    }

    const filteredExistTeam = state.filter.teams.find((team) => team.id === payload.id);
    if (filteredExistTeam) {
      const fIndex = state.filter.teams.indexOf(filteredExistTeam);
      const fUpdated = {
        ...state.filter.teams[fIndex],
        name: payload.name,
        shortName: payload.shortName,
      } as (typeof state.filter.teams)[number];
      if (payload.logo !== undefined) {
        (fUpdated as any).logo = payload.logo as any;
      }
      state.filter.teams[fIndex] = fUpdated;
    }
  });

  builder.addCase(editTeam.rejected, (state, action) => {
    state.processing = false;
    state.processedName = undefined;
    state.error = action.error.message || 'Unknown error';
  });
}

export function createDeleteTeamReducer(builder: ReducerBuilder) {
  builder.addCase(deleteTeam.pending, (state) => {
    state.processing = true;
    state.processedName = 'deleteTeam';
  });

  builder.addCase(deleteTeam.fulfilled, (state, action) => {
    state.teams = state.teams.filter((team) => team.id !== action.payload.id);

    if (state.filter.text) {
      state.filter.teams = state.filter.teams.filter((team) => team.id !== action.payload.id);
    }

    state.processing = false;
    state.processedName = undefined;
  });

  builder.addCase(deleteTeam.rejected, (state, action) => {
    state.processing = false;
    state.processedName = undefined;
    state.error = action.error.message || 'Unknown error';
  });
}

export function createUpsertTeamKitsReducer(builder: ReducerBuilder) {
	builder.addCase(upsertTeamKits.fulfilled, (state, action) => {
		const updated = action.payload;
		const idx = state.teams.findIndex((t) => t.id === updated.id);
		if (idx !== -1) {
			state.teams[idx] = { ...state.teams[idx], kits: updated.kits };
		}
		const fIdx = state.filter.teams.findIndex((t) => t.id === updated.id);
		if (fIdx !== -1) {
			state.filter.teams[fIdx] = { ...state.filter.teams[fIdx], kits: updated.kits };
		}
	});
}
